<script>
import { RecyclingFolder } from '@/models/recyclingModule/recyclingFolder/RecyclingFolder'
import ManufacturerService from '@/services/referentiel/ManufacturerService'
import { displayToast, handleResponseError, shouldShowTooltip } from '@/components/utils/globalFunction'
import RecyclingReferentialService from '@/services/recyclingModule/RecyclingReferentialService'
import RecyclingFolderService from '@/services/recyclingModule/RecyclingFolderService'
import ConfirmDialogue from '@/components/modal/ConfirmDialogue.vue'
import Validator from '@/components/utils/validator'

export default {
  name: 'StepProductPackaging',
  components: { ConfirmDialogue },
  props: {
    currentStep: Number,
    currentRecyclingFolder: {
      type: RecyclingFolder,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      formStepProductPackagingValid: false,
      dataLoaded: false,

      manufacturer: null,
      bacDetail: null,

      email: null,

      emailRules: Validator.emailRules,
      requiredRules: Validator.requiredRules,

      batteryWarning: null,

      error: null
    }
  },
  async created() {
    this.manufacturer = await ManufacturerService.getManufacturerByBoomerangId(
      this.currentRecyclingFolder.manufacturerBoomerangId
    )

    this.bacDetail = await RecyclingReferentialService.getBacDetail(
      this.currentRecyclingFolder.boomerangId
    )

    this.dataLoaded = true
  },
  methods: {
    shouldShowTooltip,

    async downloadCertificate() {
      try {
        this.loading = true

        const response = await RecyclingFolderService.getCertificate(
          this.currentRecyclingFolder.boomerangId
        )
        const filename =
          'CERTIFICAT-DE-CESSION-POUR-RECYCLAGE-' + this.currentRecyclingFolder.boomerangId + '.pdf'

        if (response != null) {
          const url = window.URL.createObjectURL(new Blob([response]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
        }
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async submit() {
      const confirm = await this.$refs.confirmDialogue.show({
        title: this.$t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.MODAL.TITLE'),
        message: this.$t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.MODAL.MESSAGE'),
        validateButton: this.$t('COMMON.BUTTON.CONFIRM'),
        cancelButton: this.$t('COMMON.BUTTON.CANCEL')
      })
      if (confirm) {
        this.loading = true
        try {
          const data = {
            recyclingFolderBoomerangId: this.currentRecyclingFolder.boomerangId,
            batteryWarning: this.batteryWarning,
            contactEmail: this.email
          }
          await RecyclingFolderService.updateRecyclingFolderProductPackaging(
            this.currentRecyclingFolder.boomerangId,
            data
          )

          displayToast(this.$t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.SUCCESS'), 'success')

          this.$router.push({ name: 'RecyclingPage' })
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      }
    }
  }
}
</script>

<template>
  <v-row class="step-container" v-if="dataLoaded">
    <v-col cols="7">
      <v-card class="step-card rounded-xl mt-4 d-flex-column" :border="false" :elevation="0">
        <v-form
          ref="formStepProductChecking"
          v-model="formStepProductPackagingValid"
          @submit.prevent="submit"
        >
          <!--TITLE CARD-->
          <v-row>
            <v-col>
              <h1 class="bold">{{ $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.TITLE') }}</h1>
            </v-col>
          </v-row>

          <!--BAC-DETAIL-->
          <v-row class="d-flex justify-center bac-container">
            <v-col>
              <!--BAC PICTURE-->
              <div class="bac-picture">
                <v-img class="bac-picture-content" :src="bacDetail.pictureUrl" alt="Bac Preview" />
              </div>

              <!--BAC DESIGNATION-->
              <div class="bac-designation">
                <p class="bold">
                  {{ bacDetail.designation }}
                </p>
              </div>

              <!--BAC LOGO RECYCLER-->
              <div class="bac-logo-recycler">
                <v-img
                  class="bac-logo-recycler-content"
                  :src="bacDetail.recyclerLogoUrl"
                  alt="Logo recycler"
                />
              </div>
            </v-col>
          </v-row>

          <!--Download Certificate-->
          <v-row>
            <v-col>
              <div class="d-flex align-center justify-start certificate-link-container">
                <v-icon icon="mdi-download" @click="downloadCertificate()" />
                <p class="certificate-link" @click="downloadCertificate()">
                  {{ $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.DOWNLOAD_LINK_TEXT') }}
                </p>
              </div>
            </v-col>
          </v-row>

          <!--Send Certificate by email-->
          <v-row>
            <v-col class="email-container">
              <p class="email-label">
                {{ $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.FORM.MAIL') }}
              </p>
              <v-text-field
                v-model="email"
                :placeholder="$t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.PLACEHOLDER.MAIL')"
                :rules="emailRules"
                :error-messages="error?.email"
              />
            </v-col>
          </v-row>

          <!--BATTERY WARNING-->
          <v-row>
            <v-col>
              <v-radio-group v-model="batteryWarning" :rules="requiredRules" hide-details>
                <v-radio
                  color="#559a1b"
                  :label="
                    $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.BATTERY_WARNING.WITHOUT_BATTERY')
                  "
                  value="WITHOUT_BATTERY"
                />

                <v-radio
                  color="#559a1b"
                  :label="
                    $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.BATTERY_WARNING.WITH_BATTERY')
                  "
                  value="WITH_BATTERY"
                />
              </v-radio-group>
            </v-col>
          </v-row>

          <!-- Validation form Button -->
          <v-row>
            <v-col cols="12" class="d-flex justify-start validation-btn-container">
              <v-btn
                :disabled="!batteryWarning || loading"
                :loading="loading"
                class="pl-15 pr-15"
                :class="!batteryWarning || loading ? 'boom-button-transparent' : 'boom-button'"
                type="submit"
                variant="outlined"
              >
                {{ $t('COMMON.BUTTON.FINISH') }}
                <img
                  alt="arrow_small-icon"
                  :class="!batteryWarning || loading ? 'arrow-black-svg' : 'arrow-white-svg'"
                  src="@/assets/icons/file/arrow_small_right.svg"
                />
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <!--PICTURE CARD-->
    <v-col cols="5">
      <v-card class="step-card rounded-xl mt-4 d-flex-column" :border="false" :elevation="0">
        <v-card
          class="product-card"
          :ripple="false"
          :id="currentRecyclingFolder.productDetail.technicalId"
        >
          <div class="picture" v-if="currentRecyclingFolder.productDetail.picture">
            <v-img
              class="picture-content"
              :src="currentRecyclingFolder.productDetail.picture"
              alt="Product Preview"
            />
          </div>

          <v-card-text>
            <v-row
              align="center"
              class="mx-0 flex-fill d-flex align-center justify-center flex-column"
            >
              <!--MANUFACTURER NAME-->
              <v-col class="d-flex align-center justify-center flex-column">
                <p class="manufacturer-name">
                  {{ manufacturer?.name }}
                </p>
              </v-col>

              <!--PRODUCT NAME-->
              <v-col class="d-flex align-center justify-center flex-column">
                <p class="product-name">
                  {{ currentRecyclingFolder.productDetail.boomerangName }}
                </p>
              </v-col>

              <v-col>
                <v-divider />
              </v-col>

              <!--CONDITION-->
              <v-col class="d-flex justify-center flex-column">
                <v-row>
                  <v-col cols="6">
                    {{ $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.CONDITION_LABEL') }}
                  </v-col>
                  <v-col cols="6" class="condition-title-col">
                    {{ currentRecyclingFolder.buybackDetail.conditionTitle }}
                  </v-col>
                </v-row>
              </v-col>

              <v-col>
                <v-divider />
              </v-col>

              <!--PRICE BUYBACK-->
              <v-col>
                <v-row>
                  <v-col cols="6">
                    {{ $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.BUYBACK_PRICE_LABEL') }}
                  </v-col>
                  <v-col cols="6" class="condition-price-col">
                    {{ currentRecyclingFolder.buybackDetail.buybackPrice }} €
                  </v-col>
                </v-row>
              </v-col>

              <v-col>
                <v-divider />
              </v-col>

              <!--BAC DESIGNATION-->
              <v-col>
                <v-row>
                  <v-col cols="6">
                    {{ $t('RECYCLING_MODULE.STEP_PRODUCT_PACKAGING.BAC_DESIGNATION_LABEL') }}
                  </v-col>
                  <v-col cols="6" class="condition-price-col">
                    {{ bacDetail.designation }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card>
    </v-col>
  </v-row>
  <ConfirmDialogue ref="confirmDialogue" />
</template>

<style scoped>
.step-container {
  min-height: 70vh;

  .step-card {
    padding: 24px;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;

    .bold {
      font-weight: bold;
    }

    .product-card {
      background-color: #efefef;
      padding: 24px;
      box-shadow: none;
      border-radius: 24px;

      .picture .picture-content {
        max-width: 350px;
        max-height: 350px;
      }

      .picture {
        margin: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        background: white;
        padding: 10px;
      }

      .manufacturer-name {
        font-size: 18px;
        font-weight: bold;
        color: #559a1b;
      }

      .product-name {
        font-size: 18px;
        font-weight: bold;
      }

      .condition-title-col,
      .condition-price-col {
        font-weight: bold;
        text-align: end;
      }
    }

    .bac-container {
      .bac-picture,
      .bac-logo-recycler {
        margin: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
      }

      .bac-picture .bac-picture-content {
        max-width: 250px;
        max-height: 250px;
      }

      .bac-logo-recycler .bac-logo-recycler-content {
        max-width: 250px;
        max-height: 50px;
      }

      .bac-designation {
        margin: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
      }
    }

    .certificate-link-container {
      color: #7d7d7d;

      .certificate-link {
        cursor: pointer;
        text-decoration: underline;
        color: #7d7d7d;
      }
    }

    .email-container {
      .email-label {
        margin-bottom: 10px;
      }
    }

    .validation-btn-container {
      .arrow-black-svg {
        height: 100%;
        width: 50%;
        filter: invert(0%) sepia(35%) saturate(13%) hue-rotate(154deg) brightness(96%)
          contrast(100%);
      }

      .arrow-white-svg {
        height: 100%;
        width: 50%;
        filter: invert(100%) sepia(0%) saturate(1%) hue-rotate(209deg) brightness(109%)
          contrast(101%);
      }

      .boom-button-transparent:hover .arrow-svg {
        filter: invert(48%) sepia(35%) saturate(7492%) hue-rotate(64deg) brightness(105%)
          contrast(79%);
      }
    }
  }
}
</style>
